import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useAuthStore } from "@/stores/auth-store"
import { useThemeStore } from "@/stores/theme-store"
import { CommandPalette } from "@/components/command-palette"
import { useNotifications, useUnreadCount, useMarkNotificationRead, useMarkAllRead } from "@/hooks/use-notifications"
import { Search, Bell, Sun, Moon, Monitor, LogOut, User, CheckCheck, Sparkles, Menu } from "lucide-react"
import { formatDate } from "@/lib/utils"
import { useAiStore } from "@/stores/ai-store"
import { useSidebarStore } from "@/stores/sidebar-store"

export function TopBar() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user, logout } = useAuthStore()
  const { theme, setTheme } = useThemeStore()
  const { toggle: toggleAi } = useAiStore()
  const { setMobileOpen } = useSidebarStore()
  const [commandOpen, setCommandOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [notificationsOpen, setNotificationsOpen] = useState(false)
  const { data: notificationsData } = useNotifications()
  const { data: unreadData } = useUnreadCount()
  const markReadMutation = useMarkNotificationRead()
  const markAllReadMutation = useMarkAllRead()

  const notifications = notificationsData?.data || []
  const unreadCount = unreadData?.count || 0

  const themeIcon =
    theme === "dark" ? Moon : theme === "light" ? Sun : Monitor
  const ThemeIcon = themeIcon

  const nextTheme = (): "light" | "dark" | "system" => {
    if (theme === "light") return "dark"
    if (theme === "dark") return "system"
    return "light"
  }

  return (
    <>
      <header
        className="flex h-10 items-center justify-between border-b bg-[var(--bg-surface)] px-4"
      >
        {/* Mobile hamburger + Search trigger */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setMobileOpen(true)}
            className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted md:hidden"
            aria-label="Otwórz menu"
          >
            <Menu size={18} />
          </button>
          <button
            onClick={() => setCommandOpen(true)}
            className="flex items-center gap-2 rounded-md border bg-[var(--bg-surface)] px-2.5 py-1 text-muted-foreground transition-colors hover:border-[var(--border-strong)]"
            aria-label={t("common.search")}
          >
            <Search size={14} />
            <span className="text-[13px]">{t("common.search")}</span>
            <kbd
              className="ml-4 hidden rounded border px-1 py-px text-[10px] font-medium text-muted-foreground sm:inline"
            >
              ⌘K
            </kbd>
          </button>
        </div>

        {/* Right actions */}
        <div className="flex items-center gap-0.5">
          {/* AI Assistant */}
          <button
            onClick={toggleAi}
            className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted"
            aria-label="OkeyAI (⌘J)"
          >
            <Sparkles size={16} />
          </button>

          {/* Notifications */}
          <div className="relative">
            <button
              onClick={() => setNotificationsOpen(!notificationsOpen)}
              className="relative flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted"
              aria-label="Powiadomienia"
            >
              <Bell size={16} />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-red-500 px-0.5 text-[9px] font-bold text-white">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </button>

            {notificationsOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setNotificationsOpen(false)} />
                <div
                  className="absolute right-0 top-full z-50 mt-1.5 flex max-h-[400px] w-80 flex-col overflow-hidden rounded-lg border bg-popover shadow-lg"
                >
                  <div className="flex items-center justify-between border-b px-3 py-2">
                    <span className="text-[13px] font-semibold text-foreground">Powiadomienia</span>
                    {unreadCount > 0 && (
                      <button
                        onClick={() => markAllReadMutation.mutate()}
                        className="flex items-center gap-1 text-[12px] text-primary transition-colors hover:opacity-80"
                      >
                        <CheckCheck size={12} /> Oznacz wszystkie
                      </button>
                    )}
                  </div>
                  <div className="flex-1 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="px-4 py-8 text-center">
                        <Bell size={20} className="mx-auto mb-2 text-muted-foreground opacity-30" />
                        <p className="text-[13px] text-muted-foreground">Brak powiadomień</p>
                      </div>
                    ) : (
                      notifications.slice(0, 20).map((n) => (
                        <button
                          key={n.id}
                          className={`flex w-full items-start gap-3 border-b px-3 py-2 text-left transition-colors hover:bg-muted/30 ${
                            n.is_read ? "" : "bg-blue-50/50 dark:bg-blue-950/20"
                          }`}
                          onClick={() => {
                            if (!n.is_read) markReadMutation.mutate(n.id)
                            setNotificationsOpen(false)
                            if (n.action_url) navigate(n.action_url)
                          }}
                        >
                          {!n.is_read && (
                            <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary" />
                          )}
                          <div className={n.is_read ? "min-w-0 flex-1 pl-4" : "min-w-0 flex-1"}>
                            <p className="truncate text-[13px] font-medium text-foreground">{n.title}</p>
                            {n.body && (
                              <p className="mt-0.5 truncate text-[12px] text-muted-foreground">{n.body}</p>
                            )}
                            <p className="mt-0.5 text-[11px] text-muted-foreground">{formatDate(n.created_at)}</p>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* User menu */}
          <div className="relative ml-1.5">
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="flex h-7 w-7 items-center justify-center rounded-full bg-[var(--bg-surface-raised)] text-[10px] font-medium text-[var(--text-secondary)] transition-shadow hover:ring-2 hover:ring-[var(--border)]"
            >
              {user
                ? `${user.first_name[0]}${user.last_name[0]}`
                : "?"}
            </button>

            {userMenuOpen && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setUserMenuOpen(false)}
                />
                <div
                  className="absolute right-0 top-full z-50 mt-1.5 w-48 overflow-hidden rounded-lg border bg-popover py-1 shadow-lg"
                >
                  {user && (
                    <div className="border-b px-3 py-2">
                      <p className="text-[13px] font-medium text-foreground">
                        {user.first_name} {user.last_name}
                      </p>
                      <p className="text-[12px] text-muted-foreground">
                        {user.email}
                      </p>
                    </div>
                  )}
                  <button
                    onClick={() => {
                      setUserMenuOpen(false)
                      navigate("/settings/profile")
                    }}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-[13px] text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground"
                  >
                    <User size={14} />
                    {t("common.profile")}
                  </button>
                  <button
                    onClick={() => setTheme(nextTheme())}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-[13px] text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground"
                  >
                    <ThemeIcon size={14} />
                    {t("theme." + theme, theme === "light" ? "Jasny" : theme === "dark" ? "Ciemny" : "System")}
                  </button>
                  <button
                    onClick={() => {
                      setUserMenuOpen(false)
                      logout()
                    }}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-[13px] text-red-500 transition-colors hover:bg-muted/50"
                  >
                    <LogOut size={14} />
                    {t("common.logout")}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      <CommandPalette open={commandOpen} onOpenChange={setCommandOpen} />
    </>
  )
}
